import { Alert, AlertTitle, Box, Link, Stack, Typography, ViewObserver, toast } from '@montugroup/design-system';
import { useTheme } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';

import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import ManagedErrorToasts from '@/components/error/ManagedErrorToasts';
import HelpdeskWidget from '@/components/helpdesk/HelpdeskSelector';
import { BackButton } from '@/components/order/BackButton';
import { OrderSummaryButton } from '@/components/order/order-summary/OrderSummaryButton';
import RefillOrderSummary from '@/components/order/order-summary/RefillOrderSummary';
import { UseOrDeleteExistingOrderModal } from '@/components/order/UseOrDeleteExistingOrderModal';
import { GENERATIVE, OUT_OF_STOCK } from '@/components/patient/enquiryModal/common';
import EnquiryErrorDialog from '@/components/patient/enquiryModal/EnquiryErrorDialog';
import EnquiryLoadingDialog from '@/components/patient/enquiryModal/EnquiryLoadingDialog';
import RequestConfirmationDialog from '@/components/patient/enquiryModal/requstConfirmationDialog/RequestConfirmationDialog';
import { PurchaseStepper } from '@/components/products/purchaseStepper/PurchaseStepper';
import ProductCategoryAccordion from '@/components/products/refill/ProductCategoryAccordion';
import ProductRefillCard from '@/components/products/refill/ProductRefillCard';
import { ProductList, ProductSection } from '@/components/products/refill/ProductSection';
import type { UnavailableProductIssue } from '@/components/products/refill/UnavailableProductRefillCard';
import { UnavailableProductRefillCard } from '@/components/products/refill/UnavailableProductRefillCard';
import {
  FF_BRAZE_CONTENT_CARDS,
  FF_ENABLE_CONCESSION_DISCOUNT_BANNER,
  FF_INLINE_UNAVAILABLE_PRODUCTS,
} from '@/constants/featureFlags';
import { RECOMMENDED_DEVICE_MAX_QUANTITY, RRP_DISCLAIMER } from '@/constants/order';
import PageLoadError from '@/hooks/patient/refill/page-errors';
import { useCrossSell } from '@/hooks/patient/refill/useCrossSell';
import { useOrderMedicationController } from '@/hooks/patient/refill/useOrderMedicationController';
import { FORMULATION_ID_DEVICE } from '@/hooks/patient/refill/util';
import useCreatePatientRescriptRequest from '@/hooks/patient/useCreatePatientRescriptRequest';
import { useGetCartEventInfo } from '@/hooks/products/useCartEventInfo';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useCustomer from '@/hooks/user/useCustomer';
import {
  formatDateForDisplay,
  getProductPurchaseIssue,
  mapToProductCard,
  mapToProductRefillCardPrescription,
  mapToProductRefillCardProduct,
  sortUnavailableProducts,
} from '@/pages/umeds/order-medication/util';
import { PROFILE_DETAILS_ROUTE } from '@/routes/route-paths';
import { RescriptRequestReasons } from '@/services/patient.service';
import { fetchProductEnquiryStatus } from '@/services/product.service';
import { numberFormat } from '@/utils/helpers';
import { Logger } from '@/utils/logger';
import { isProductInIntervalLockedState, isProductInIntervalPreOrderState } from '@/utils/order';
import { getImageUrlWithFallback } from '@montugroup/circuit-shared/components/product-detail/utils';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import type { ProductEnquiryStatus } from '@montugroup/circuit-shared/types/enquiry';
import type { RefillResponseProduct } from '@montugroup/circuit-shared/types/refill';
import { SasApprovalAlert } from '../../../components/banners/SasApprovalAlert';
import { getCheckoutData } from '../../../hooks/patient/refill/getCheckoutData';
import {
  DesktopOnly,
  DisclaimerRRP,
  MobileOnlySticky,
  PageContent,
  PageSection,
  StickyBox,
} from './OrderMedication.styles';
import PatientRefillLoadingSkeleton from './OrderMedicationLoadingSkeleton';
import RequestReplacementDialog from './RequestReplacementDialog';

const logger = new Logger('PatientRefill');

const contactLink = 'https://www.alternaleaf.com.au/contact-us';

/// /////////////////
// Types
/// /////////////////

interface ProductIssuePair {
  product: RefillResponseProduct;
  issue: UnavailableProductIssue | null;
}

/// //////////////
// UTILITY FUNCTIONS
/// //////////////

const generateProductIssuePairs = (products: RefillResponseProduct[]): ProductIssuePair[] =>
  products.map((product) => {
    const issue = getProductPurchaseIssue(product);
    return {
      product,
      issue,
    };
  });

// Show PIR button for any device/medication with a valid prescription
export const shouldShowPIR = (product: RefillResponseProduct) =>
  product.prescription_id !== undefined && product.prescription_id !== null;

/// ////////////
// Component
/// ////////////

function OrderMedication() {
  /**
   * ***********
   * Hooks
   ************
   */
  const { data: user } = useUser();
  const { data: customer, isLoading: customerIsLoading } = useCustomer();
  const { data: userProfileData, isLoading: userProfileDataIsLoading } = useUserProfileData();
  const createPatientRescriptRequest = useCreatePatientRescriptRequest();
  const { flags } = useFeatureFlags();
  const theme = useTheme();

  /**
   * ***********
   * Route Params
   *************
   */
  const patientRefillController = useOrderMedicationController();
  const { refillResponse, isSubmitting, selectedProducts, hasSelectedAnyProducts, creditDiscounts, needSasApproval } =
    patientRefillController.state;
  const { submitOrder, validateOrder, enqueueError } = patientRefillController.actions;

  const { attemptCrossSell, showCrossSellPage, crossSellableProducts, resetCrossSell, getRecommendation } =
    useCrossSell(selectedProducts.map((product) => ({ ...product, quantity: product.data.quantity })));

  /**
   * ***********
   * Component State
   *************
   */

  const [selectedProductId, setSelectedProductId] = useState<number>();

  const [isCheckProductEnquiryLoading, setIsCheckProductEnquiryLoading] = useState(false);
  const [isEnquiryProductDialogOpen, setIsEnquiryProductDialogOpen] = useState(false);
  const [productEnquiryStatus, setProductEnquiryStatus] = useState<ProductEnquiryStatus | null>();
  const [isRequestConfirmationDialogOpen, setIsRequestConfirmationDialogOpen] = useState(false);
  const [hasEnquiryError, setHasEnquiryError] = useState(false);
  const [isCreateRescriptRequestLoading, setIsCreateRescriptRequestLoading] = useState<boolean>();

  /**
   * *************
   * Derived State
   **************
   */

  const checkoutData = getCheckoutData(selectedProducts, creditDiscounts);
  const isDischarged = customer?.isDischarged;

  // umeds reports the prescribed per-order quantity in the payload — matching
  // the events the controller used to send.
  const getCartEventInfo = useGetCartEventInfo(refillResponse.data?.products, { reportPrescribedQuantity: true });

  const isCheckoutDisabled = customerIsLoading || isDischarged || isSubmitting || !hasSelectedAnyProducts;
  const selectedProduct = mapToProductCard(
    (refillResponse.data?.products || []).find((product) => product.id === selectedProductId),
  );

  const hasV1Script = refillResponse.data?.hasV1Script ?? false;

  // Divide Products into Available and Unavailable
  const productIssuePairs = generateProductIssuePairs(refillResponse.data?.products || []);
  const unavailableProductsWithIssues = productIssuePairs.filter((pip) => pip.issue !== null);

  // Flag ON: unavailable items render inline at the bottom of the section that
  // would have held them, replacing the "Can't order now" accordion.
  const inlineUnavailableProducts = Boolean(flags[FF_INLINE_UNAVAILABLE_PRODUCTS]);
  const unavailableMedications = sortUnavailableProducts(
    unavailableProductsWithIssues.filter((pip) => pip.product.formulation_id !== FORMULATION_ID_DEVICE),
  );
  const unavailableDevices = unavailableProductsWithIssues.filter(
    (pip) => pip.product.formulation_id === FORMULATION_ID_DEVICE,
  );
  const unavailablePrescribedDevices = sortUnavailableProducts(
    unavailableDevices.filter((pip) => Boolean(pip.product.prescription_id)),
  );
  const unavailableOtherDevices = sortUnavailableProducts(
    unavailableDevices.filter((pip) => !pip.product.prescription_id),
  );

  // All Products
  const availableProducts = productIssuePairs.filter((pip) => pip.issue === null).map((pip) => pip.product);

  // Divide into Devices and Medication
  const availProdDevices = availableProducts.filter((x) => x.formulation_id === FORMULATION_ID_DEVICE);
  const availProdNonDevices = availableProducts.filter((x) => x.formulation_id !== FORMULATION_ID_DEVICE);

  // Divide devices into Prescribed and Non-Prescribed
  const prescribedDevices = availProdDevices.filter((x) => Boolean(x.prescription_id));
  const nonPrescribedDevices = availProdDevices.filter((x) => !x.prescription_id);

  const shouldShowSasAlert = Boolean(needSasApproval);
  // check for concession status
  const isConcessionEnabled = flags[FF_ENABLE_CONCESSION_DISCOUNT_BANNER];
  const shouldShowConcessionBanner = (isConcessionEnabled && customer?.isConcession) || false;

  const ConcessionAlert = () => {
    return (
      <ViewObserver id={`refill-concession-alert-${customer?.id ?? ''}`}>
        <Box sx={{ my: 4 }}>
          <Alert severity="info">
            <Typography variant="inherit">Concession benefits applied.</Typography>
          </Alert>
        </Box>
      </ViewObserver>
    );
  };

  /*
    This PR https://github.com/MontuGroup/montu-web/pull/4458 removed the moreUserData from localStorage.
    However, some users may still have it in localStorage as we are no longer removing it.

    This is a temporary fix to remove the moreUserData if found from localStorage.
    We need to remove this once we see no logs of moreUserData in Datadog.

    Yard work ticket to remove this once we see no logs of moreUserData in Datadog: https://montugroup.atlassian.net/browse/UMED-6115
  */
  if (localStorage.getItem('moreUserData')) {
    logger.info(`moreUserData found, removing for userId=${user?.id ?? 'unknown'}`);
    localStorage.removeItem('moreUserData');
  }

  // Respond to data being loaded. Nothing is pre-selected: order pre-filling was
  // removed on both customer types (#7647), so the quantity store stays empty
  // until the patient picks something.
  useEffect(() => {
    if (typeof refillResponse.data === 'undefined') {
      logger.info(`userId=${user!.id} loaded refill page`);
    }
  }, [refillResponse.data]);

  useEffect(() => {
    if (userProfileDataIsLoading) {
      return;
    }
    if (!userProfileData?.user.phone) {
      toast.warning(
        {
          title: '',
          message: (
            <span>
              Please add your phone number to your <Link href={PROFILE_DETAILS_ROUTE}>profile</Link>
            </span>
          ),
        },
        { autoClose: false },
      );
    }
  }, [userProfileDataIsLoading, userProfileData]);
  /**
   * **********
   * Callbacks
   ************
   */

  const handleCheckProductStatus = useCallback(
    (product: RefillResponseProduct) => async () => {
      setIsCheckProductEnquiryLoading?.(true);
      setSelectedProductId?.(product.id);
      try {
        const isOutOfStock = product.is_out_of_stock || product.reasoning_toggle;
        const rescriptRequestReason = isOutOfStock ? OUT_OF_STOCK : GENERATIVE;
        const data = await fetchProductEnquiryStatus(
          refillResponse.data?.patient.id,
          product.prescription_id,
          product.id,
          rescriptRequestReason,
        );
        setProductEnquiryStatus?.({
          ...data,
          rescriptActionDate: data.rescriptActionDate ? moment(data.rescriptActionDate).format('DD/MM/YYYY') : null,
          rescriptRequestDate: data.rescriptRequestDate ? moment(data.rescriptRequestDate).format('DD/MM/YYYY') : null,
        });
        setIsCheckProductEnquiryLoading?.(false);
        setIsEnquiryProductDialogOpen?.(true);
      } catch {
        setIsCheckProductEnquiryLoading?.(false);
        setHasEnquiryError?.(true);
      }
    },
    [
      refillResponse.data,
      setIsCheckProductEnquiryLoading,
      setSelectedProductId,
      setIsEnquiryProductDialogOpen,
      setHasEnquiryError,
    ],
  );

  // Ordering process is currently Find Existing Order => Validate Order => Place Order
  const handleSubmit = async () => {
    const isCrossSelling = !showCrossSellPage && (await attemptCrossSell());

    if (showCrossSellPage || !isCrossSelling) {
      try {
        await submitOrder({
          validateOrderCallback: validateOrder,
        });
        showCrossSellPage && resetCrossSell();
      } catch (error: any) {
        enqueueError({
          title: 'Error Submitting Order',
          body: error.message,
        });
      }
    }
  };

  const handleRequestAlternativeProduct = () => {
    setIsEnquiryProductDialogOpen(false);
    setIsRequestConfirmationDialogOpen(true);
  };

  const handleOnSendRequest = async () => {
    setIsCreateRescriptRequestLoading(true);

    try {
      // Todo: Can we assume that data.patient is always populated at this point?
      const newPatientRescriptRequest = await createPatientRescriptRequest(refillResponse.data!.patient!.id, {
        prescriptionId: selectedProduct!.prescriptionId!,
        productId: selectedProduct!.id,
        rescriptRequestReason: selectedProduct!.isGenerativeScripting
          ? RescriptRequestReasons.Discontinued
          : RescriptRequestReasons.OutOfStock,
      });

      setIsCreateRescriptRequestLoading(false);
      setIsRequestConfirmationDialogOpen(false);
      setIsEnquiryProductDialogOpen(true);
      setProductEnquiryStatus({
        ...(productEnquiryStatus || {}),
        rescriptRequestDate: moment(newPatientRescriptRequest.created_date).format('DD/MM/YYYY'),
      });
    } catch {
      setIsCreateRescriptRequestLoading(false);
      setHasEnquiryError(true);
    }
  };

  /**
   * **********
   * Render Logic
   ************
   */

  const title = showCrossSellPage ? 'Consider with your treatment' : 'Order medication';

  const renderUnavailableProductCard = (pip: ProductIssuePair) => {
    const isInDispenseInterval = isProductInIntervalLockedState(pip.product);
    const formattedReorderDate =
      isInDispenseInterval && pip.product.intervalDetails?.estimatedNextProcessDate
        ? formatDateForDisplay(pip.product.intervalDetails.estimatedNextProcessDate)
        : undefined;

    return (
      pip.issue && (
        <UnavailableProductRefillCard
          key={pip.product.id}
          product={pip.product}
          issue={pip.issue}
          handleCheckProductStatus={handleCheckProductStatus(pip.product)}
          showRequestReplacementOption={shouldShowPIR(pip.product)}
          showDispenseInterval={isInDispenseInterval}
          reorderDate={formattedReorderDate}
        />
      )
    );
  };

  const hasVisibleMedicationCards =
    availProdNonDevices.length > 0 || (inlineUnavailableProducts && unavailableMedications.length > 0);
  const prescribedDevicesCount =
    prescribedDevices.length + (inlineUnavailableProducts ? unavailablePrescribedDevices.length : 0);
  const otherProductsCount =
    nonPrescribedDevices.length + (inlineUnavailableProducts ? unavailableOtherDevices.length : 0);

  return (
    <>
      <ManagedErrorToasts />
      {showCrossSellPage && <BackButton action={resetCrossSell} />}
      {refillResponse.isLoading && <PatientRefillLoadingSkeleton />}
      {!refillResponse.isLoading && (
        <PageContent data-dd-privacy="allow" data-testid="umedsOrderMedicationPage">
          {refillResponse.isSuccess && (
            <>
              <PageSection sx={{ gridArea: 'title' }}>
                <Typography variant="h5" component="h1" fontWeight={(theme) => theme.typography.fontWeightBold}>
                  {title}
                </Typography>
                <SasApprovalAlert enabled={shouldShowSasAlert} />
                {shouldShowConcessionBanner && <ConcessionAlert />}
              </PageSection>
              <PageSection sx={{ gridArea: 'steps', mb: 3 }}>
                <Stack direction="column" gap={theme.spacing(6)}>
                  {flags[FF_BRAZE_CONTENT_CARDS] && <BrazeImageCard />}
                  <PurchaseStepper stage="Order" />
                </Stack>
              </PageSection>
              {showCrossSellPage && crossSellableProducts && Boolean(crossSellableProducts?.length) ? (
                <PageSection sx={{ gridArea: 'products' }}>
                  <ProductList
                    products={crossSellableProducts.map((crossSellableProduct, position) => ({
                      ...crossSellableProduct,
                      productName: crossSellableProduct.name,
                      supplier: crossSellableProduct.supplierName || crossSellableProduct.brand || undefined,
                      maxQuantity: RECOMMENDED_DEVICE_MAX_QUANTITY,
                      cartEventInfo: getCartEventInfo(crossSellableProduct.id, getRecommendation(position)),
                      ...(crossSellableProduct.imageUrl && {
                        image: {
                          src: getImageUrlWithFallback({
                            imageUrl: crossSellableProduct.imageUrl,
                            width: 60,
                            height: 60,
                          }),
                        },
                      }),
                    }))}
                  />
                </PageSection>
              ) : (
                <PageSection sx={{ gridArea: 'products' }}>
                  {availProdNonDevices.map((product) => {
                    const isInDispenseInterval = isProductInIntervalPreOrderState(product);
                    const formattedReorderDate =
                      isInDispenseInterval && product.intervalDetails?.estimatedNextProcessDate
                        ? formatDateForDisplay(product.intervalDetails.estimatedNextProcessDate)
                        : undefined;

                    return (
                      <ProductRefillCard
                        key={product.id}
                        product={mapToProductRefillCardProduct(product)}
                        prescription={mapToProductRefillCardPrescription(product)}
                        cartEventInfo={getCartEventInfo(product.id)}
                        showDispenseInterval={isInDispenseInterval}
                        reorderDate={formattedReorderDate}
                      />
                    );
                  })}
                  {inlineUnavailableProducts && unavailableMedications.map(renderUnavailableProductCard)}
                  {hasV1Script ? (
                    <Alert variant="outlined" severity="warning">
                      <Typography variant="body2" component="div">
                        <Typography component="span" fontWeight="bold">
                          Prescription not found
                        </Typography>
                        <br />
                        <Link href={contactLink} target="_blank" rel="noopener noreferrer">
                          Contact the clinic
                        </Link>{' '}
                        to reissue your prescription.
                      </Typography>
                    </Alert>
                  ) : hasVisibleMedicationCards ? null : (
                    <Alert variant="outlined" severity="info">
                      You currently have no prescribed medication available
                    </Alert>
                  )}
                  {inlineUnavailableProducts || unavailableProductsWithIssues.length === 0 ? null : (
                    <ProductCategoryAccordion
                      label="Can't order now"
                      count={unavailableProductsWithIssues.length}
                      startCollapsed={!(unavailableProductsWithIssues.length > 0 && availProdNonDevices.length === 0)}
                    >
                      {unavailableProductsWithIssues.map(renderUnavailableProductCard)}
                    </ProductCategoryAccordion>
                  )}
                  {prescribedDevicesCount > 0 && (
                    <ProductSection
                      label="Prescribed devices"
                      count={prescribedDevicesCount}
                      products={prescribedDevices.map((product) => ({
                        ...product,
                        productName: product.name,
                        supplier: product.brand || undefined,
                        maxQuantity: RECOMMENDED_DEVICE_MAX_QUANTITY,
                        cartEventInfo: getCartEventInfo(product.id),
                      }))}
                    >
                      {inlineUnavailableProducts && unavailablePrescribedDevices.map(renderUnavailableProductCard)}
                    </ProductSection>
                  )}
                  {otherProductsCount > 0 && (
                    <ProductSection
                      label="Other Products"
                      count={otherProductsCount}
                      products={nonPrescribedDevices.map((product) => ({
                        ...product,
                        productName: product.name,
                        supplier: product.brand || undefined,
                        maxQuantity: RECOMMENDED_DEVICE_MAX_QUANTITY,
                        cartEventInfo: getCartEventInfo(product.id),
                      }))}
                    >
                      {inlineUnavailableProducts && unavailableOtherDevices.map(renderUnavailableProductCard)}
                    </ProductSection>
                  )}
                </PageSection>
              )}
              <Box sx={{ gridArea: 'purchase' }}>
                <StickyBox>
                  <DesktopOnly>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <RefillOrderSummary
                        checkoutData={checkoutData}
                        onCheckout={handleSubmit}
                        disabled={isCheckoutDisabled}
                      />
                    </Box>
                  </DesktopOnly>
                  <DisclaimerRRP hasSelectedAnyProducts={hasSelectedAnyProducts}>
                    <Typography variant="caption">{RRP_DISCLAIMER}</Typography>
                  </DisclaimerRRP>
                </StickyBox>
              </Box>
            </>
          )}
          {refillResponse.isError && (
            <Alert severity="error">
              <AlertTitle>{PageLoadError.title}</AlertTitle>
              <div>{PageLoadError.body}</div>
            </Alert>
          )}
        </PageContent>
      )}
      <EnquiryLoadingDialog
        open={isCheckProductEnquiryLoading}
        onClose={() => setIsCheckProductEnquiryLoading(false)}
      />
      <EnquiryErrorDialog open={hasEnquiryError} onClose={() => setHasEnquiryError(false)} />
      {selectedProduct && selectedProductId && productEnquiryStatus && (
        <RequestReplacementDialog
          open={isEnquiryProductDialogOpen}
          onClose={() => {
            setIsEnquiryProductDialogOpen(false);
          }}
          product={selectedProduct}
          productEnquiryStatus={productEnquiryStatus}
          handleRequestAlternativeProduct={handleRequestAlternativeProduct}
          handleCheckProductStatus={handleCheckProductStatus(selectedProduct as unknown as RefillResponseProduct)} // This is yuck, checking if there's a mapping function
        />
      )}
      <RequestConfirmationDialog
        open={isRequestConfirmationDialogOpen}
        onClose={() => setIsRequestConfirmationDialogOpen(false)}
        onSendRequest={handleOnSendRequest}
        loading={isCreateRescriptRequestLoading}
      />
      <HelpdeskWidget bottomOffset={hasSelectedAnyProducts ? 80 : undefined} />

      {hasSelectedAnyProducts && (
        <MobileOnlySticky>
          <OrderSummaryButton
            totalNumberOfProducts={checkoutData.totalNumberOfProducts}
            onClick={handleSubmit}
            amount={numberFormat(checkoutData.totalPrice)}
            disabled={isCheckoutDisabled}
          />
        </MobileOnlySticky>
      )}

      <UseOrDeleteExistingOrderModal />
    </>
  );
}

export default OrderMedication;
